import type { ApolloError } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import debounce from 'awesome-debounce-promise';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useState } from 'react';

import type {
  AuthenticationInfoQuery,
  AuthenticationInfoQueryVariables,
} from 'src/shared/graphql/generated';
import { AuthenticationInfoDocument as query } from 'src/shared/graphql/generated';
import { useEphemeralEmail } from 'src/shared/hooks';
import { logFactory } from 'src/shared/log';

import {
  Provider as SingleSignOnProvider,
  defaultValue,
} from '../../hooks/useSingleSignOn';

interface ProviderProps {
  isActive?: boolean;
  redirectPath?: string;
  children: ReactNode;
}

const QUERY_DEBOUNCE_WAIT_MILLISECONDS = 500;
const log = logFactory('login:providers:SingleSignOn');

const Provider = ({
  children,
  isActive = false,
  redirectPath,
}: ProviderProps) => {
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);
  const [signInUrl, setSignInUrl] = useState<string | null>();
  const [isEnabled, setIsEnabled] = useState(defaultValue.isEnabled);

  const {
    email: ephemeralEmail,
    setEmail: setEphemeralEmail,
  } = useEphemeralEmail();

  const [currentEmail, setCurrentEmail] = useState<string | undefined>(
    ephemeralEmail,
  );

  const setEmail = (email?: string) => {
    setEphemeralEmail(email);
    setCurrentEmail(email);
  };

  const [authenticationInfoError, setAuthenticationInfoError] = useState<
    ApolloError | undefined
  >(undefined);

  const checkEmail = debounce(async (email: string) => {
    log('📩 Setting current email...', email);
    setEmail(email);

    try {
      log('🔃 Set isLoading...', true);
      setIsLoading(true);

      log('🔭 Querying sso info...', { email });
      const { data } = await client.query<
        AuthenticationInfoQuery,
        AuthenticationInfoQueryVariables
      >({ query, variables: { email, redirectPath } });

      log(`📨 SSO response received...`, data?.authenticationInfo);
      const ssoEnabled = data?.authenticationInfo.ssoEnabled ?? false;

      log(`🔐 Set isEnabled...`, ssoEnabled);
      setIsEnabled(ssoEnabled);
      setSignInUrl(data?.authenticationInfo.ssoSpSigninUrl);

      setAuthenticationInfoError(undefined);

      return ssoEnabled;
    } catch (error) {
      log(`❗ Setting error...`, error);
      setAuthenticationInfoError(error);

      log('🔓 Set sso disabled as a result of an error being thrown');
      setIsEnabled(false);
      setSignInUrl(null);

      return false;
    } finally {
      log('🏁 Set isLoading...', false);
      setIsLoading(false);
    }
  }, QUERY_DEBOUNCE_WAIT_MILLISECONDS);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkEmailMemoized = useCallback(checkEmail, [client]);

  useEffect(() => {
    log('🔐 SSO isActive updated...', isActive);
    setAuthenticationInfoError(undefined);
  }, [isActive, setAuthenticationInfoError]);

  const contextValue = {
    ...defaultValue,
    email: currentEmail,
    error: authenticationInfoError,
    isActive,
    isEnabled,
    isLoading,
    checkEmail: checkEmailMemoized,
    signInUrl,
  };

  return (
    <SingleSignOnProvider value={contextValue}>{children}</SingleSignOnProvider>
  );
};

export { Provider };
